import { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Users, AlertTriangle, Plus, X, Check, AlertCircle, Shield, Hexagon, Link2, Zap, Building, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Deployments({ user }) {
  const [activeTab, setActiveTab] = useState('sites');
  const [sites, setSites] = useState([]);
  const [teams, setTeams] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [siteForm, setSiteForm] = useState({
    site_code: '',
    site_name: '',
    client_name: '',
    location_address: '',
    location_city: '',
    location_state: '',
    risk_classification: 'Medium',
    is_mixed_deployment: false,
    site_classification: 'STANDARD',
  });

  const [incidentForm, setIncidentForm] = useState({
    site_id: '',
    severity: 'Medium',
    title: '',
    description: '',
    location_details: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sitesRes, teamsRes, incidentsRes, personnelRes] = await Promise.all([
        axios.get(`${API}/sites?include_all_doctrines=true`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/incidents?include_all_doctrines=true`),
        axios.get(`${API}/personnel`),
      ]);
      setSites(sitesRes.data);
      setTeams(teamsRes.data);
      setIncidents(incidentsRes.data);
      setPersonnel(personnelRes.data);
    } catch (error) {
      console.error('Failed to fetch deployment data:', error);
      setError(error.response?.data?.detail || 'Failed to load deployment data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSite = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/sites`, siteForm);
      setSuccess('Site created successfully!');
      setShowSiteModal(false);
      setSiteForm({ site_code: '', site_name: '', client_name: '', location_address: '', location_city: '', location_state: '', risk_classification: 'Medium', is_mixed_deployment: false, site_classification: 'STANDARD' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create site');
    }
  };

  const handleCreateIncident = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const result = await axios.post(`${API}/incidents`, incidentForm);
      let msg = 'Incident reported successfully!';
      if (result.data.collision_warning) {
        msg += ' ⚠️ Collision detected with another doctrine\'s report.';
      }
      setSuccess(msg);
      setShowIncidentModal(false);
      setIncidentForm({ site_id: '', severity: 'Medium', title: '', description: '', location_details: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to report incident');
    }
  };

  const getRiskColor = (risk) => {
    const colors = { Low: 'text-emerald-400', Medium: 'text-amber-400', High: 'text-orange-400', Critical: 'text-red-400' };
    return colors[risk] || 'text-slate-400';
  };

  const getSeverityClass = (severity) => {
    const classes = { Low: 'severity-low', Medium: 'severity-medium', High: 'severity-high', Critical: 'severity-critical' };
    return classes[severity] || 'severity-medium';
  };

  const tabs = [
    { id: 'sites', label: 'SITES', icon: Building, count: sites.length },
    { id: 'incidents', label: 'INCIDENTS', icon: AlertTriangle, count: incidents.length },
    { id: 'teams', label: 'TEAMS', icon: Users, count: teams.length },
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="spinner mb-4"></div>
        <p className="text-cyan-400 text-sm tracking-wider animate-pulse">LOADING DEPLOYMENTS...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="deployments-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-wide mb-2" style={{ fontFamily: 'ui-monospace, monospace', textShadow: '0 0 20px rgba(6, 182, 212, 0.4)' }}>
            DEPLOYMENTS
          </h1>
          <p className="text-slate-500 text-sm tracking-wider">OPERATIONS & FIELD MANAGEMENT</p>
        </div>
        <div className="flex gap-3">
          {user.rank?.level >= 4 && (
            <button onClick={() => setShowSiteModal(true)} className="btn-primary flex items-center gap-2">
              <Plus size={16} />
              NEW SITE
            </button>
          )}
          <button onClick={() => setShowIncidentModal(true)} className="btn-tactical flex items-center gap-2">
            <AlertTriangle size={16} />
            REPORT INCIDENT
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 animate-fade-in">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-400 text-sm">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300"><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 animate-fade-in">
          <Check className="w-4 h-4 text-emerald-400" />
          <span className="text-emerald-400 text-sm">{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto text-emerald-400 hover:text-emerald-300"><X size={16} /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-800 pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 border-b-0'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 font-mono">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Sites Tab */}
      {activeTab === 'sites' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sites.map(site => (
            <SiteCard key={site.id} site={site} getRiskColor={getRiskColor} />
          ))}
          {sites.length === 0 && (
            <div className="col-span-full text-center py-12 glass-panel">
              <MapPin className="w-12 h-12 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-500">No deployment sites found</p>
            </div>
          )}
        </div>
      )}

      {/* Incidents Tab */}
      {activeTab === 'incidents' && (
        <div className="glass-panel overflow-hidden">
          <table className="tactical-table">
            <thead>
              <tr>
                <th>Incident #</th>
                <th>Doctrine</th>
                <th>Site</th>
                <th>Title</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Collision</th>
              </tr>
            </thead>
            <tbody>
              {incidents.map(incident => (
                <tr key={incident.id}>
                  <td className="mono">{incident.incident_number}</td>
                  <td>
                    {incident.doctrine && (
                      <span className={`doctrine-badge ${incident.doctrine?.code === 'GDT' ? 'doctrine-gdt' : 'doctrine-pist'}`} style={{ padding: '2px 8px', fontSize: '10px' }}>
                        <Hexagon size={10} />
                        {incident.doctrine?.code}
                      </span>
                    )}
                  </td>
                  <td>{incident.site_code || incident.site_name}</td>
                  <td className="text-white">{incident.title}</td>
                  <td>
                    <span className={`px-2 py-1 rounded text-[10px] font-bold ${getSeverityClass(incident.severity)}`}>
                      {incident.severity?.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <span className={`text-xs ${incident.status === 'Open' ? 'text-amber-400' : incident.status === 'Closed' ? 'text-slate-500' : 'text-blue-400'}`}>
                      {incident.status}
                    </span>
                  </td>
                  <td>
                    {incident.collision_status !== 'NONE' && (
                      <span className="collision-indicator">
                        <Link2 size={10} />
                        {incident.collision_status}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {incidents.length === 0 && (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-500">No incidents reported</p>
            </div>
          )}
        </div>
      )}

      {/* Teams Tab */}
      {activeTab === 'teams' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {teams.map(team => (
            <TeamCard key={team.id} team={team} />
          ))}
          {teams.length === 0 && (
            <div className="col-span-full text-center py-12 glass-panel">
              <Users className="w-12 h-12 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-500">No teams assigned</p>
            </div>
          )}
        </div>
      )}

      {/* Site Modal */}
      {showSiteModal && (
        <Modal title="CREATE DEPLOYMENT SITE" onClose={() => setShowSiteModal(false)}>
          <form onSubmit={handleCreateSite} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Site Code</label>
                <input type="text" value={siteForm.site_code} onChange={(e) => setSiteForm({...siteForm, site_code: e.target.value})} className="input-tactical" required placeholder="SITE-001" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Site Name</label>
                <input type="text" value={siteForm.site_name} onChange={(e) => setSiteForm({...siteForm, site_name: e.target.value})} className="input-tactical" required />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Client Name</label>
              <input type="text" value={siteForm.client_name} onChange={(e) => setSiteForm({...siteForm, client_name: e.target.value})} className="input-tactical" required />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Address</label>
              <input type="text" value={siteForm.location_address} onChange={(e) => setSiteForm({...siteForm, location_address: e.target.value})} className="input-tactical" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">City</label>
                <input type="text" value={siteForm.location_city} onChange={(e) => setSiteForm({...siteForm, location_city: e.target.value})} className="input-tactical" required />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">State</label>
                <input type="text" value={siteForm.location_state} onChange={(e) => setSiteForm({...siteForm, location_state: e.target.value})} className="input-tactical" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Risk Level</label>
                <select value={siteForm.risk_classification} onChange={(e) => setSiteForm({...siteForm, risk_classification: e.target.value})} className="input-tactical">
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Classification</label>
                <select value={siteForm.site_classification} onChange={(e) => setSiteForm({...siteForm, site_classification: e.target.value})} className="input-tactical">
                  <option value="STANDARD">Standard</option>
                  <option value="RESTRICTED">Restricted</option>
                  <option value="CLASSIFIED">Classified</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-800">
              <input type="checkbox" id="mixed" checked={siteForm.is_mixed_deployment} onChange={(e) => setSiteForm({...siteForm, is_mixed_deployment: e.target.checked})} className="w-4 h-4 accent-cyan-500" />
              <label htmlFor="mixed" className="text-sm text-slate-300">Mixed Deployment Site (Multiple Doctrines)</label>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <button type="button" onClick={() => setShowSiteModal(false)} className="btn-tactical">CANCEL</button>
              <button type="submit" className="btn-primary">CREATE SITE</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Incident Modal */}
      {showIncidentModal && (
        <Modal title="REPORT INCIDENT" onClose={() => setShowIncidentModal(false)}>
          <form onSubmit={handleCreateIncident} className="space-y-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Site</label>
              <select value={incidentForm.site_id} onChange={(e) => setIncidentForm({...incidentForm, site_id: e.target.value})} className="input-tactical" required>
                <option value="">Select Site</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>{site.site_code} - {site.site_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Title</label>
              <input type="text" value={incidentForm.title} onChange={(e) => setIncidentForm({...incidentForm, title: e.target.value})} className="input-tactical" required />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Description</label>
              <textarea value={incidentForm.description} onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})} className="input-tactical h-24" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Severity</label>
                <select value={incidentForm.severity} onChange={(e) => setIncidentForm({...incidentForm, severity: e.target.value})} className="input-tactical">
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Location Details</label>
                <input type="text" value={incidentForm.location_details} onChange={(e) => setIncidentForm({...incidentForm, location_details: e.target.value})} className="input-tactical" placeholder="Building, floor, area..." />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <button type="button" onClick={() => setShowIncidentModal(false)} className="btn-tactical">CANCEL</button>
              <button type="submit" className="btn-primary flex items-center gap-2">
                <Zap size={16} />
                SUBMIT REPORT
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function SiteCard({ site, getRiskColor }) {
  return (
    <div className="glass-panel p-5 hover:border-cyan-500/30 transition-all">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono text-cyan-400">{site.site_code}</span>
            {site.is_mixed_deployment && (
              <span className="mixed-site-badge">
                <Hexagon size={10} />
                MIXED
              </span>
            )}
          </div>
          <h3 className="text-white font-semibold">{site.site_name}</h3>
        </div>
        <span className={`text-xs font-bold ${getRiskColor(site.risk_classification)}`}>
          {site.risk_classification?.toUpperCase()}
        </span>
      </div>
      <p className="text-slate-400 text-sm mb-3">{site.client_name}</p>
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <MapPin size={12} />
        {site.location_city}, {site.location_state}
      </div>
      {site.primary_doctrine && (
        <div className="mt-3 pt-3 border-t border-slate-800/50 flex items-center justify-between">
          <span className={`doctrine-badge ${site.primary_doctrine?.code === 'GDT' ? 'doctrine-gdt' : 'doctrine-pist'}`} style={{ padding: '2px 8px', fontSize: '10px' }}>
            <Hexagon size={10} />
            {site.primary_doctrine?.code}
          </span>
          <span className="text-xs text-slate-500 font-mono">{site.status}</span>
        </div>
      )}
    </div>
  );
}

function TeamCard({ team }) {
  return (
    <div className="glass-panel p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-semibold">{team.team_name}</h3>
        {team.doctrine && (
          <span className={`doctrine-badge ${team.doctrine?.code === 'GDT' ? 'doctrine-gdt' : 'doctrine-pist'}`} style={{ padding: '2px 8px', fontSize: '10px' }}>
            <Hexagon size={10} />
            {team.doctrine?.code}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
        <Users size={14} />
        {team.member_count || team.team_members?.length || 0} members
      </div>
      <div className="text-xs text-slate-500">
        NCOIC: {team.ncoic?.full_name || 'Not assigned'}
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content p-6 max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white tracking-wider flex items-center gap-2">
            <Shield className="text-cyan-400" size={20} />
            {title}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default Deployments;
